// Layout shell — 52px header with logo + wordmark on the left and, on the
// right: [analyze] (or its inline progress pill), [update] (stub), theme
// toggle, logout. Content renders through the router <Outlet/>.

import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";

import { apiFetch } from "../api/client";
import { useAnalyzeStatus } from "../api/hooks";
import type { AnalyzeStatus } from "../api/types";
import { getStoredTheme, setStoredTheme, type Theme } from "../theme";
import { Logo } from "./Logo";
import { YoutubeModeButton } from "../youtube/YoutubeModeButton"; // experimental, removable

import "./Layout.css";

export function Layout() {
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());
  const navigate = useNavigate();

  function toggleTheme() {
    const next: Theme = theme === "light" ? "dark" : "light";
    setTheme(next);
    setStoredTheme(next);
  }

  async function logout() {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // Even if the request fails, drop to the login screen.
    }
    navigate("/login", { replace: true });
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link to="/" className="app-header__logo" aria-label="cinematic database — home">
          <span className="app-header__glyph">
            <Logo size={20} />
          </span>
          <span className="app-header__wordmark">cinematic database</span>
        </Link>

        <div className="app-header__spacer" />

        <button
          type="button"
          className="app-header__btn app-header__btn--primary"
          title="step through the queue — rate or skip each suggestion"
          onClick={() => navigate("/?list=queue&rate=1")}
        >
          rate
        </button>

        <AnalyzeControl />

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          disabled
          title="run update in the terminal app"
        >
          update
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          title="queue statistics — how each fully rated queue scored"
          onClick={() => navigate("/stats")}
        >
          stats
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          title="download your full database as a CSV file — all lists, ratings, and metadata"
          onClick={() => {
            // Plain navigation: the endpoint answers with Content-Disposition:
            // attachment, so the browser saves the file and stays on the page.
            window.location.href = "/api/export/csv";
          }}
        >
          export
        </button>

        <YoutubeModeButton />

        <button
          type="button"
          className="app-header__btn app-header__btn--ghost"
          onClick={toggleTheme}
          aria-label={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
        >
          {theme === "light" ? "☾" : "☀"}
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--ghost app-header__btn--settings"
          title="settings — taste profile"
          aria-label="Settings"
          onClick={() => navigate("/settings")}
        >
          ⚙
        </button>

        <button
          type="button"
          className="app-header__btn app-header__btn--outline"
          onClick={() => void logout()}
        >
          logout
        </button>
      </header>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

// ── Analyze button / progress pill / result toasts ─────────────────────────

interface ToastState {
  kind: "ok" | "error";
  text: string;
}

function phaseText(status: AnalyzeStatus): string {
  const done = status.progress?.done ?? 0;
  const total = status.progress?.total ?? 0;
  switch (status.phase) {
    case "profile":
      return "updating taste profile…";
    case "matching":
      return `matching ${done}/${total}`;
    case "enriching":
      return `fetching covers ${done}/${total}`;
    default:
      return "asking claude…";
  }
}

function AnalyzeControl() {
  const { data: status, mutate: mutateStatus } = useAnalyzeStatus();
  const { mutate } = useSWRConfig();
  const [toast, setToast] = useState<ToastState | null>(null);
  const prevState = useRef<AnalyzeStatus["state"] | null>(null);
  const toastTimer = useRef<number | null>(null);

  function showToast(next: ToastState, ms = 5000) {
    if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    setToast(next);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }

  // Toast only on a transition observed in this session (running → done/error)
  // so a stale "done" from a previous run doesn't toast on page load.
  useEffect(() => {
    const state = status?.state;
    if (!state) return;
    if (prevState.current === "running" && state === "done") {
      showToast({ kind: "ok", text: `added ${status?.added ?? 0} titles` });
      void mutate("/api/lists");
      void mutate("/api/lists/queue");
    } else if (prevState.current === "running" && state === "error") {
      showToast({ kind: "error", text: status?.error ?? "analyze failed" });
    }
    prevState.current = state;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.state]);

  useEffect(() => {
    return () => {
      if (toastTimer.current != null) window.clearTimeout(toastTimer.current);
    };
  }, []);

  async function startAnalyze() {
    try {
      const res = await apiFetch("/api/analyze", { method: "POST" });
      if (res.status === 409) {
        showToast({ kind: "ok", text: "already running" }, 2500);
      }
      void mutateStatus();
    } catch {
      showToast({ kind: "error", text: "could not start analyze" });
    }
  }

  const running = status?.state === "running";
  const total = status?.progress?.total ?? 0;
  const done = status?.progress?.done ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : null;

  return (
    <>
      {running && status ? (
        <div className="analyze-pill" role="status">
          <span className="analyze-pill__label">{phaseText(status)}</span>
          <span className="analyze-pill__track" aria-hidden>
            <span
              className={
                "analyze-pill__bar" + (pct == null ? " analyze-pill__bar--indeterminate" : "")
              }
              style={pct == null ? undefined : { width: `${pct}%` }}
            />
          </span>
        </div>
      ) : (
        <button
          type="button"
          className="app-header__btn app-header__btn--primary"
          onClick={() => void startAnalyze()}
        >
          analyze
        </button>
      )}

      {toast && (
        <div
          className={`toast toast--${toast.kind}`}
          role={toast.kind === "error" ? "alert" : "status"}
        >
          {toast.text}
        </div>
      )}
    </>
  );
}
